import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatLegacyAutocompleteTrigger as MatAutocompleteTrigger } from '@angular/material/legacy-autocomplete';
import { UntilDestroy } from '@ngneat/until-destroy';
import { connectState, InputObservable, trackByValue } from '@y42/angular/util';
import { ColumnTypeEnum, OperatorEnum } from '@y42/json-schema';
import { distinctUntilArrayChanged } from '@y42/rxjs';
import { findUniqueValues } from '@y42/util/find-unique-values';
import { normalizeString } from '@y42/util/normalize-string';
import { isNotNullish } from '@y42/util/nullish';
import { SelectOption } from '@y42/util/select-option';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  startWith,
  switchMap,
} from 'rxjs';
import { OperatorForm } from '../../../operation-input.model';
import { operationInputDefaultOperatorOptions } from '../../operation-input-operator-form-control/operation-input-default-operator-options';

@UntilDestroy()
@Component({
  selector: 'y42-operation-input-text-form-group[form][index][columnType]',
  templateUrl: './operation-input-text-form-group.component.html',
  styleUrls: ['../../operation-input-shared.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OperationInputTextFormGroupComponent {
  private readonly autoProposalLoading$$ = new BehaviorSubject(false);

  @Input() columnType!: ColumnTypeEnum;

  @Input()
  @InputObservable()
  form!: FormGroup<OperatorForm>;
  form$!: Observable<FormGroup<OperatorForm>>;

  @Input() index!: number;

  @Input() enableMoreConditions = false;

  @Input()
  @InputObservable()
  operatorOptions?: Array<SelectOption<OperatorEnum>>;
  operatorOptions$!: Observable<Array<SelectOption<OperatorEnum>> | undefined>;

  private readonly predefinedValues$$ = new BehaviorSubject<string[] | null>(null);
  @Input()
  set predefinedValues(predefinedValues: string[] | null) {
    this.autoProposalLoading$$.next(false);
    this.predefinedValues$$.next(predefinedValues);
  }
  get predefinedValues() {
    return this.predefinedValues$$.getValue();
  }

  @Output() loadAutoProposal = new EventEmitter<void>();

  @ViewChild(MatAutocompleteTrigger) autoComplete?: MatAutocompleteTrigger;

  private readonly defaultOperatorOptions: Array<SelectOption<OperatorEnum>> = operationInputDefaultOperatorOptions;

  readonly trackByValue = trackByValue;
  readonly OperatorEnum = OperatorEnum;

  private readonly isSingleValueOperation$ = this.form$.pipe(
    filter(isNotNullish),
    switchMap((form) => {
      if (!form.controls.operator) {
        return of(false);
      }
      return form.controls.operator.valueChanges.pipe(
        startWith(form.controls.operator.value),
        filter(isNotNullish),
        map(
          (operator) =>
            ![OperatorEnum.In, OperatorEnum.NotIn, OperatorEnum.IsEmpty, OperatorEnum.IsNotEmpty].includes(operator),
        ),
      );
    }),
    startWith(false),
    distinctUntilChanged(),
  );

  private readonly isMultiValueOperation$ = this.form$.pipe(
    filter(isNotNullish),
    switchMap((form) => {
      if (!form.controls.operator) {
        return of(false);
      }
      return form.controls.operator.valueChanges.pipe(
        startWith(form.controls.operator.value),
        filter(isNotNullish),
        map((operator) => [OperatorEnum.In, OperatorEnum.NotIn].includes(operator)),
      );
    }),
    startWith(false),
    distinctUntilChanged(),
  );

  readonly state = connectState(this, {
    isSingleValueOperation: this.isSingleValueOperation$,
    isMultiValueOperation: this.isMultiValueOperation$,
    autoProposalLoading: this.autoProposalLoading$$,
    autoProposalLoaded: combineLatest([this.autoProposalLoading$$, this.predefinedValues$$]).pipe(
      map(([isLoading, predefinedValues]) => {
        if (predefinedValues === null) {
          return false;
        }
        return !isLoading;
      }),
    ),
    autocompleteValues: combineLatest([
      this.predefinedValues$$.pipe(map((values) => values || [])),
      this.isMultiValueOperation$.pipe(
        switchMap((isMultiValueOperation) =>
          isMultiValueOperation
            ? this.valuesInputCtrl.valueChanges.pipe(startWith(this.valuesInputCtrl.value))
            : this.form$.pipe(
                filter(isNotNullish),
                switchMap((form) =>
                  form.controls.values.valueChanges.pipe(
                    startWith(form.controls.values.value),
                    map((values) => values[0]),
                  ),
                ),
              ),
        ),
        startWith(undefined),
      ),
    ]).pipe(
      map(([predefinedValues, input]) => {
        if (!input) {
          return predefinedValues;
        }
        return findUniqueValues(
          predefinedValues.filter((v) => normalizeString(`${v}`).includes(normalizeString(`${input}`))),
        );
      }),
      distinctUntilArrayChanged(),
    ),
    operatorOptions: this.operatorOptions$.pipe(map((options) => options ?? this.defaultOperatorOptions)),
  });

  readonly valuesInputCtrl = new FormControl('', { nonNullable: true });

  disableAutoComplete = false;

  setAutoProposalLoading() {
    this.autoProposalLoading$$.next(true);
  }

  toggleAutocomplete() {
    this.disableAutoComplete = !this.disableAutoComplete;
    if (this.disableAutoComplete) {
      this.closeAutocompletePanel();
    }
  }

  closeAutocompletePanel() {
    this.autoComplete?.closePanel();
  }

  patchValues(value: unknown) {
    this.form.controls.values.setValue([value]);
  }
}
